import * as React from "react";
import { XIcon } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export type WorkspaceDrawerCloseButtonProps = {
  onClose: () => void;
  className?: string;
  "aria-label"?: string;
} & Omit<React.ComponentProps<"button">, "type" | "onClick" | "children" | "className" | "aria-label">;

export function WorkspaceDrawerCloseButton({
  onClose,
  className,
  "aria-label": ariaLabel = "Close drawer",
  ...props
}: WorkspaceDrawerCloseButtonProps): React.ReactElement {
  return (
    <button
      type="button"
      data-workspace-node-drawer-close=""
      aria-label={ariaLabel}
      className={cn(
        "inline-flex size-8 shrink-0 items-center justify-center rounded-md text-[#6b7280] transition-colors",
        "hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "dark:text-white dark:hover:bg-muted/50",
        className
      )}
      {...props}
      onClick={onClose}
    >
      <XIcon className="size-4" aria-hidden />
    </button>
  );
}

/**
 * Hover / focus label for icon-only drawer toolbar buttons.
 * Parent should wrap buttons with `TooltipProvider` (see `WorkspaceDrawerActionRow`).
 */
export function DrawerIconButtonTooltip({
  label,
  children,
  side = "bottom",
}: {
  label: string;
  children: React.ReactElement;
  side?: "top" | "bottom" | "left" | "right";
}): React.ReactElement {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side} sideOffset={6}>
        <span className="font-[family-name:var(--font-primary)] text-xs font-medium normal-case tracking-normal">
          {label}
        </span>
      </TooltipContent>
    </Tooltip>
  );
}

export type WorkspaceDrawerActionRowProps = {
  children?: React.ReactNode;
  onClose?: () => void;
  className?: string;
};

/** Action buttons on the left; optional drawer close on the right. */
export function WorkspaceDrawerActionRow({
  children,
  onClose,
  className,
}: WorkspaceDrawerActionRowProps): React.ReactElement {
  const actions = (
    <TooltipProvider delayDuration={300}>{children}</TooltipProvider>
  );

  if (!onClose) {
    return (
      <div className={cn("flex justify-end", className)}>
        <div className="flex flex-wrap items-center gap-3">{actions}</div>
      </div>
    );
  }

  return (
    <div
      data-workspace-drawer-action-row=""
      className={cn("flex items-start justify-between gap-3", className)}
    >
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">{actions}</div>
      <WorkspaceDrawerCloseButton onClose={onClose} className="mt-0" />
    </div>
  );
}
